// Every Rise Vision template is 1920x1080 (1080x1920 on portrait displays), so screen shares are sent as a constant
// frame of that size.
// Some hardware decoders (e.g. MediaTek on Avocor K-series) fail permanently when the incoming H.264 frame
// size changes mid-stream, which happens whenever the shared tab/window is resized or a video goes fullscreen.
// Compositing the capture onto a fixed black canvas keeps the encoded size constant while still showing resizes.
import {
  createLetterboxTransform,
  FIXED_FRAME_HEIGHT,
  FIXED_FRAME_WIDTH,
} from './fixed-frame-compose';

export {
  computeLetterbox,
  FIXED_FRAME_HEIGHT,
  FIXED_FRAME_WIDTH,
  getFixedFrameSize,
} from './fixed-frame-compose';
export type { FrameOrientation, FrameRect } from './fixed-frame-compose';

// Insertable Streams for MediaStreamTrack (Chromium). Not in lib.dom yet, so declare the parts we use.
interface TrackProcessor {
  readable: ReadableStream<VideoFrame>;
}
interface TrackGenerator extends MediaStreamTrack {
  writable: WritableStream<VideoFrame>;
}
interface InsertableStreamsGlobals {
  MediaStreamTrackProcessor?: new (init: {
    track: MediaStreamTrack;
  }) => TrackProcessor;
  MediaStreamTrackGenerator?: new (init: { kind: 'video' }) => TrackGenerator;
  OffscreenCanvas?: typeof OffscreenCanvas;
  VideoFrame?: typeof VideoFrame;
}

export function isFixedFrameSupported(): boolean {
  const globals = globalThis as unknown as InsertableStreamsGlobals;
  return (
    typeof globals.MediaStreamTrackProcessor === 'function' &&
    typeof globals.MediaStreamTrackGenerator === 'function' &&
    typeof globals.OffscreenCanvas === 'function' &&
    typeof globals.VideoFrame === 'function'
  );
}

// How long to wait for the worker script to load before compositing on the page instead.
const WORKER_READY_TIMEOUT_MS = 2_000;

/**
 * Starts the compositing worker and hands it the streams once it has loaded. Calls `onFallback` (with the streams
 * still usable on the page) if workers aren't available, the script fails to load, or the transfer fails. Calls
 * `onFailure` if the worker's pipeline fails after it took the streams, when they can no longer be used here.
 */
function startWorker(
  readable: ReadableStream<VideoFrame>,
  writable: WritableStream<VideoFrame>,
  frameWidth: number,
  frameHeight: number,
  onFallback: (reason: unknown) => void,
  onFailure: (reason: unknown) => void,
  onStillChange: (still: boolean) => void,
): Worker | null {
  if (typeof Worker !== 'function') {
    onFallback('Worker not supported');
    return null;
  }

  let worker: Worker;
  try {
    worker = new Worker(new URL('./fixed-frame.worker', import.meta.url), {
      type: 'module',
    });
  } catch (error) {
    onFallback(error);
    return null;
  }

  let transferred = false;
  let fellBack = false;
  const fallBack = (reason: unknown) => {
    if (transferred || fellBack) {
      return;
    }
    fellBack = true;
    clearTimeout(readyTimeout);
    worker.terminate();
    onFallback(reason);
  };
  const readyTimeout = setTimeout(
    () => fallBack('worker did not load in time'),
    WORKER_READY_TIMEOUT_MS,
  );

  worker.addEventListener('error', (event) => {
    const reason = event.message || 'worker error';
    if (transferred) {
      onFailure(reason);
    } else {
      fallBack(reason);
    }
  });
  worker.addEventListener('message', (event: MessageEvent) => {
    const type = event.data?.type;
    if (type === 'ready' && !transferred && !fellBack) {
      try {
        // Transferring moves the streams to the worker; they can't be used on the page afterwards.
        worker.postMessage({ readable, writable, frameWidth, frameHeight }, [
          readable as unknown as Transferable,
          writable as unknown as Transferable,
        ]);
        transferred = true;
        clearTimeout(readyTimeout);
      } catch (error) {
        fallBack(error);
      }
    } else if (type === 'error' || type === 'stopped') {
      onFailure(event.data?.message ?? type);
    } else if (type === 'still') {
      onStillChange(!!event.data?.still);
    }
  });

  return worker;
}

/**
 * Draws every captured frame onto a fixed-size black canvas and exposes the result as a video track.
 *
 * The drawing runs in a dedicated worker. The Rise Apps tab is hidden whenever the user shares another tab or their
 * screen, and Chrome deprioritizes a hidden page's main thread, which dropped the stream to a few fps when this ran
 * on the page. The page is only used as a fallback when a worker can't be started.
 *
 * `onFailure` is called once if the pipeline stops producing frames while the video is in use (the worker crashed or
 * its pipeline errored, or the page pipeline failed). The track is dead from then on, so the owner has to replace it.
 */
export class FixedFrameVideo {
  readonly track: MediaStreamTrack;
  private readonly generator: TrackGenerator;
  private readonly input: MediaStreamTrack;
  private worker: Worker | null = null;
  // Set once the pipeline runs on the page; the worker's pipeline is refreshed through a message instead.
  private refreshOnPage: (() => void) | null = null;
  private stopped = false;
  private failed = false;
  private stillScreen = false;

  constructor(
    source: MediaStreamTrack,
    private readonly frameWidth = FIXED_FRAME_WIDTH,
    private readonly frameHeight = FIXED_FRAME_HEIGHT,
    private readonly onFailure?: (reason: unknown) => void,
    {
      // false composites on the page from the start, e.g. to retry after the worker pipeline failed.
      useWorker = true,
      // Called when the captured screen goes still (only keepalives are sent) or starts changing again.
      onStillChange,
    }: { useWorker?: boolean; onStillChange?: (still: boolean) => void } = {},
  ) {
    const globals = globalThis as unknown as InsertableStreamsGlobals;
    // Each pipeline reads its own clone, so landscape and portrait pipelines can share one capture.
    this.input = source.clone();
    let processor: TrackProcessor;
    try {
      processor = new globals.MediaStreamTrackProcessor!({
        track: this.input,
      });
      this.generator = new globals.MediaStreamTrackGenerator!({
        kind: 'video',
      });
    } catch (error) {
      // The caller falls back to the raw capture; don't leave this clone keeping the capture source alive.
      this.input.stop();
      throw error;
    }
    this.track = this.generator;

    const setStill = (still: boolean) => {
      if (this.stopped || still === this.stillScreen) {
        return;
      }
      this.stillScreen = still;
      onStillChange?.(still);
    };

    const composeOnPage = (reason: unknown) => {
      this.worker = null;
      if (this.stopped) {
        return;
      }
      console.warn(
        'Fixed-frame worker unavailable; compositing on the page instead.',
        reason,
      );
      try {
        processor.readable
          .pipeThrough(
            createLetterboxTransform(
              frameWidth,
              frameHeight,
              undefined,
              (fn) => {
                this.refreshOnPage = fn;
              },
              undefined,
              undefined,
              setStill,
            ),
          )
          .pipeTo(this.generator.writable)
          .catch((error) => this.fail(error));
      } catch (error) {
        this.fail(error);
      }
    };

    if (!useWorker) {
      composeOnPage('page compositing requested');
      return;
    }

    this.worker = startWorker(
      processor.readable,
      this.generator.writable,
      frameWidth,
      frameHeight,
      composeOnPage,
      (reason) => this.fail(reason),
      setStill,
    );
  }

  private fail(reason: unknown): void {
    if (this.stopped || this.failed) {
      return;
    }
    this.failed = true;
    console.warn('Fixed-frame video pipeline failed', reason);
    // Async, so the owner sees it after the constructor returned even when the failure happens during it.
    queueMicrotask(() => {
      if (!this.stopped) {
        this.onFailure?.(reason);
      }
    });
  }

  get size(): { width: number; height: number } {
    return { width: this.frameWidth, height: this.frameHeight };
  }

  get usesWorker(): boolean {
    return !!this.worker;
  }

  /** Whether the captured screen is still, so only keepalives of the last frame are being sent. */
  get still(): boolean {
    return this.stillScreen;
  }

  /** Re-sends the last frame now instead of on the next keepalive, e.g. for a connection that just came up. */
  refresh(): void {
    if (this.stopped) {
      return;
    }
    if (this.worker) {
      this.worker.postMessage({ type: 'refresh' });
    } else {
      this.refreshOnPage?.();
    }
  }

  stop(): void {
    this.stopped = true;
    this.worker?.terminate();
    this.generator.stop();
    this.input.stop();
  }
}
